import { DatePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ChartData, ChartOptions } from 'chart.js';
import { lastValueFrom, map } from 'rxjs';
import { PageableRequest } from 'src/app/admin-api';
import { SmsAnalyticsControllerService } from 'src/app/admin-api/api/smsAnalyticsController.service';
import { SmsVerifyAnalyticsCountRate } from 'src/app/admin-api/model/smsVerifyAnalyticsCountRate';
import { SmsVerifyAnalyticsCountRateDetails } from 'src/app/admin-api/model/smsVerifyAnalyticsCountRateDetails';
import { SmsVerifyAnalyticsResponse } from 'src/app/admin-api/model/smsVerifyAnalyticsResponse';
import {
  DropdownFilter,
  PageContent,
  TableColumn
} from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  SmsOtpErrorItem,
  SmsOtpErrorService
} from 'src/app/services/sms-otp-error.service';

interface SmsSummaryCard {
  title: string;
  value: string;
  description: string;
  headerClass: string;
  isRate?: boolean;
}

interface SmsDetailRow {
  label: string;
  count: string;
  rate: string;
  detailsLabel: string;
}

@Component({
  selector: 'app-sms-dashboard',
  templateUrl: './sms-dashboard.component.html',
  styleUrl: './sms-dashboard.component.scss'
})
export class SmsDashboardComponent implements OnInit {
  private smsAnalyticsService = inject(SmsAnalyticsControllerService);
  smsOtpErrorService = inject(SmsOtpErrorService);
  private datePipe = inject(DatePipe);
  private title = inject(Title);
  private numberFormatter = new Intl.NumberFormat('pt-BR');

  today = new Date();
  dateFilterForm = new FormGroup({
    startDate: new FormControl<Date>(this.daysAgo(7), Validators.required),
    endDate: new FormControl<Date>(new Date(), Validators.required)
  });

  analytics?: SmsVerifyAnalyticsResponse;
  errorsModalVisible = false;
  errorCols: Array<TableColumn> = this.buildErrorColumns();
  channelDropdownFilters: { [field: string]: Array<DropdownFilter> } = {
    channel: [{ label: 'SMS', value: 'SMS' }]
  };
  summaryCards: SmsSummaryCard[] = [];
  droppedRows: SmsDetailRow[] = [];
  undeliveredRows: SmsDetailRow[] = [];
  unverifiedRows: SmsDetailRow[] = [];
  chartData?: ChartData<'bar'>;
  chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        titleColor: '#495057',
        bodyColor: '#495057'
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#495057',
          font: {
            family: 'Inter, Montserrat, "Segoe UI", sans-serif',
            weight: 500
          }
        },
        grid: {
          color: '#ebedef'
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#495057',
          font: {
            family: 'Inter, Montserrat, "Segoe UI", sans-serif',
            weight: 500
          }
        },
        grid: {
          color: '#ebedef'
        }
      }
    }
  };

  constructor() {
    this.title.setTitle('Dashboard de SMS');
  }

  async ngOnInit(): Promise<void> {
    await this.loadAnalytics();
  }

  openErrorsModal(): void {
    if (this.dateFilterForm.invalid) return;
    this.errorsModalVisible = true;
  }

  findErrorsPage = (
    request: PageableRequest
  ): Promise<PageContent<SmsOtpErrorItem>> => this.loadErrorsPage(request);

  async loadErrorsPage(
    request: PageableRequest
  ): Promise<PageContent<SmsOtpErrorItem>> {
    return lastValueFrom(
      this.smsOtpErrorService
        .listErrors({
          ...request,
          startDate: this.dateFilterForm.controls.startDate.value,
          endDate: this.dateFilterForm.controls.endDate.value
        })
        .pipe(map((response) => response.result || {}))
    );
  }

  async loadAnalytics(loader = true): Promise<void> {
    if (this.dateFilterForm.invalid) return;

    if (loader) LoaderService.showLoader();
    try {
      this.analytics = undefined;
      this.analytics = await lastValueFrom(
        this.smsAnalyticsService
          .getVerifyAnalytics({
            startDate: this.dateFilterForm.controls.startDate.value,
            endDate: this.dateFilterForm.controls.endDate.value
          })
          .pipe(map((response) => response.result))
      );
      this.buildViewModel();
    } catch (error) {
      this.clearViewModel();
      AppDialogService.showErrorDialog(error);
    } finally {
      if (loader) LoaderService.showLoader(false);
    }
  }

  private buildViewModel(): void {
    const delivered = this.analytics?.delivery?.count?.delivered;
    const undelivered = this.analytics?.delivery?.count?.undelivered;
    const verified = this.analytics?.verification?.count?.verified;
    const unverified = this.analytics?.verification?.count?.unverified;

    this.summaryCards = [
      {
        title: 'Códigos enviados',
        value: this.formatNumber(this.analytics?.sent),
        description: 'Total enviado no período',
        headerClass: 'header--primary'
      },
      {
        title: 'Códigos descartados',
        value: this.formatNumber(this.analytics?.dropped),
        description: 'Descartados antes da entrega',
        headerClass: 'header--warning'
      },
      {
        title: 'Taxa de entrega',
        value: this.formatRate(this.analytics?.delivery?.rate),
        description: this.formatCountPair(
          delivered,
          'entregues',
          undelivered,
          'não entregues'
        ),
        headerClass: 'header--success',
        isRate: true
      },
      {
        title: 'Taxa de verificação',
        value: this.formatRate(this.analytics?.verification?.rate),
        description: this.formatCountPair(
          verified,
          'verificados',
          unverified,
          'não verificados'
        ),
        headerClass: 'header--bag',
        isRate: true
      }
    ];

    this.droppedRows = [
      this.detailRow(
        'Erros internos',
        this.analytics?.droppedMessages?.internalErrors
      ),
      this.detailRow(
        'Códigos de país inválidos',
        this.analytics?.droppedMessages?.countryCodeDrops
      ),
      this.detailRow(
        'Telefones inválidos',
        this.analytics?.droppedMessages?.invalidPhoneNumbers
      )
    ];

    this.undeliveredRows = [
      this.detailRow(
        'Violações da operadora',
        this.analytics?.undeliveredMessages?.carrierViolations
      ),
      this.detailRow(
        'Rejeições definitivas',
        this.analytics?.undeliveredMessages?.hardBounces
      ),
      this.detailRow(
        'Rejeições temporárias',
        this.analytics?.undeliveredMessages?.softBounces
      ),
      this.detailRow(
        'Falhas de entrega',
        this.analytics?.undeliveredMessages?.deliveryFailures
      ),
      this.detailRow(
        'Relatório de entrega ausente',
        this.analytics?.undeliveredMessages?.deliveryReportMissing
      )
    ];

    this.unverifiedRows = [
      this.detailRow(
        'Falhas na tentativa',
        this.analytics?.unverifiedMessages?.attemptFailures
      ),
      this.detailRow(
        'Expiração por timeout',
        this.analytics?.unverifiedMessages?.timeoutFailures
      )
    ];

    this.chartData = {
      labels: [
        'Códigos enviados',
        'Códigos descartados',
        'Códigos entregues',
        'Códigos não entregues',
        'Códigos verificados',
        'Códigos não verificados'
      ],
      datasets: [
        {
          label: 'Códigos',
          data: [
            this.analytics?.sent || 0,
            this.analytics?.dropped || 0,
            delivered || 0,
            undelivered || 0,
            verified || 0,
            unverified || 0
          ],
          backgroundColor: [
            '#5da5da',
            '#faa43a',
            '#2ecc71',
            '#ff6b6b',
            '#9b8cff',
            '#607d8b'
          ],
          borderColor: 'rgba(255, 255, 255, 0.18)',
          borderWidth: 1
        }
      ]
    };
  }

  private clearViewModel(): void {
    this.summaryCards = [];
    this.droppedRows = [];
    this.undeliveredRows = [];
    this.unverifiedRows = [];
    this.chartData = undefined;
  }

  private detailRow(
    label: string,
    item?: SmsVerifyAnalyticsCountRate | SmsVerifyAnalyticsCountRateDetails
  ): SmsDetailRow {
    return {
      label,
      count: this.formatNumber(item?.count),
      rate: this.formatRate(item?.rate),
      detailsLabel: this.detailLabel(
        (item as SmsVerifyAnalyticsCountRateDetails)?.details
      )
    };
  }

  private detailLabel(details?: Record<string, unknown>): string {
    if (!details) return '';
    return Object.entries(details)
      .map(([key, value]) => `${key}: ${this.detailValue(value)}`)
      .join(', ');
  }

  private detailValue(value: unknown): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  private formatNumber(value?: number): string {
    return value === null || value === undefined
      ? '-'
      : this.numberFormatter.format(value);
  }

  private formatRate(value?: string): string {
    return value || '-';
  }

  private formatCountPair(
    firstValue: number | undefined,
    firstLabel: string,
    secondValue: number | undefined,
    secondLabel: string
  ): string {
    const first = `${this.formatNumber(firstValue)} ${firstLabel}`;
    const second = `${this.formatNumber(secondValue)} ${secondLabel}`;
    return `${first} / ${second}`;
  }

  private daysAgo(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
  }

  private buildErrorColumns(): Array<TableColumn> {
    return [
      new TableColumn.Builder()
        .setHeader('PersonId')
        .setField('personId')
        .setFilter(true)
        .setType('number')
        .setCondition('equals')
        .setDisplayFunction((item: SmsOtpErrorItem) => this.emptyDash(item.personId))
        .build(),
      new TableColumn.Builder()
        .setHeader('Phone')
        .setField('phone')
        .setFilter(true)
        .setType('text')
        .setCondition('contains')
        .build(),
      new TableColumn.Builder()
        .setHeader('Channel')
        .setField('channel')
        .setFilter(true)
        .setType('text')
        .setCondition('equals')
        .build(),
      new TableColumn.Builder()
        .setHeader('Email')
        .setField('email')
        .setFilter(true)
        .setType('text')
        .setCondition('contains')
        .setDisplayFunction((item: SmsOtpErrorItem) => this.emptyDash(item.email))
        .build(),
      new TableColumn.Builder()
        .setHeader('LastError')
        .setField('lastError')
        .setFilter(true)
        .setType('text')
        .setCondition('contains')
        .build(),
      new TableColumn.Builder()
        .setHeader('Attempt')
        .setField('attempt')
        .setFilter(true)
        .setType('number')
        .setCondition('equals')
        .build(),
      this.dateColumn('CreatedAt', 'createdAt'),
      this.dateColumn('UpdatedAt', 'updatedAt'),
      this.dateColumn('ExpiresAt', 'expiresAt'),
      this.dateColumn('VerifiedAt', 'verifiedAt')
    ];
  }

  private dateColumn(header: string, field: keyof SmsOtpErrorItem): TableColumn {
    return new TableColumn.Builder()
      .setHeader(header)
      .setField(field)
      .setFilter(true)
      .setType('date')
      .setCondition('equals')
      .setDisplayFunction((item: SmsOtpErrorItem) => this.formatDate(item[field]))
      .build();
  }

  private formatDate(value: unknown): string {
    if (value === null || value === undefined || value === '') return '-';
    return this.datePipe.transform(value as string | number | Date, 'dd/MM/yyyy HH:mm') || '-';
  }

  private emptyDash(value: unknown): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  }
}
